import { berlinDatum, berlinUhrzeit, berlinZeitpunktAus } from "@dj/domain/gemeinsam";
import { useState } from "react";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { Leer } from "./teile";

/**
 * Jagdzeitplan (oberflaeche.md §6 Planung, §8 Nr. 3, V-24; jagdzustand/01, /05): `dialog` „Jagdzeitplan“ mit den
 * Jagdabschnitten des Jagddatums. Der Jagdherr des Veranstalters stellt die Abschnitte zusammen (`textbox` „Beginn“ und
 * „Ende“ als Uhrzeit, `button` „Hinzufügen“, je Abschnitt `button` „Entfernen“) und speichert sie als einen Befehl:
 * `JagdzeitplanFestlegen`, solange es keinen Jagdzeitplan gibt, danach `JagdzeitplanAendern` mit optionalem `textbox`
 * „Grund“ (benachrichtigt alle Mitwirkenden). Alle anderen Leser sehen die Abschnitte nur. Ob die Abschnitte gelten
 * (am Jagddatum, Beginn vor Ende, ohne Überschneidung), entscheidet der Server mit benanntem Grund.
 */
export interface Abschnitt { Beginn: string; Ende: string }

/** Jagddatum aus dem Kopf (`JJJJ-MM-TT` bzw. Zeitpunkt) als `JJJJ-MM-TT`. */
export function jagddatumAus(w: unknown): string | null {
  if (w instanceof Date) return berlinDatum(w);
  return typeof w === "string" && /^\d{4}-\d{2}-\d{2}$/.test(w) ? w : null;
}

/** Abschnitte des Lesemodells (Zeitpunkte) als Uhrzeiten. */
export function abschnitteAus(plan: unknown): Abschnitt[] {
  if (!Array.isArray(plan)) return [];
  return plan.filter((a): a is { Beginn: Date; Ende: Date } => a?.Beginn instanceof Date && a?.Ende instanceof Date)
    .map((a) => ({ Beginn: berlinUhrzeit(a.Beginn), Ende: berlinUhrzeit(a.Ende) }));
}

/** `Jagdabschnitte` des Befehls aus Uhrzeiten am Jagddatum, nach Beginn geordnet; `null`, wenn eine Uhrzeit ungültig ist. */
export function jagdabschnitte(jagddatum: string, abschnitte: readonly Abschnitt[]): Array<{ Beginn: string; Ende: string }> | null {
  const zeiten = abschnitte.map((a) => [berlinZeitpunktAus(jagddatum, a.Beginn), berlinZeitpunktAus(jagddatum, a.Ende)] as const);
  if (zeiten.some(([b, e]) => !b || !e)) return null;
  return zeiten.map(([b, e]) => ({ Beginn: b!.toISOString(), Ende: e!.toISOString() })).sort((x, y) => x.Beginn.localeCompare(y.Beginn));
}

export function JagdzeitplanDialog({ id, offen, jagddatum, bearbeitbar, onSchliessen }: {
  id: string; offen: boolean; jagddatum: string | null; bearbeitbar: boolean; onSchliessen: () => void;
}) {
  const zustand = useDokument(offen ? `drueckjagden/${id}/jagdzustand/aktuell` : null);
  const festgelegt = abschnitteAus(zustand.daten?.["Jagdzeitplan"]);
  const [entwurf, setEntwurf] = useState<Abschnitt[] | null>(null);
  const [beginn, setBeginn] = useState("");
  const [ende, setEnde] = useState("");
  const [grund, setGrund] = useState("");
  const [abweisung, setAbweisung] = useState<string | null>(null);
  const abschnitte = entwurf ?? festgelegt;
  const aendern = festgelegt.length > 0;

  function schliessen() {
    setEntwurf(null); setBeginn(""); setEnde(""); setGrund(""); setAbweisung(null);
    onSchliessen();
  }

  function hinzufuegen() {
    if (!jagddatum || !jagdabschnitte(jagddatum, [{ Beginn: beginn, Ende: ende }])) { setAbweisung("unzulaessige-angaben"); return; }
    setAbweisung(null);
    setEntwurf([...abschnitte, { Beginn: beginn.trim(), Ende: ende.trim() }].sort((a, b) => a.Beginn.padStart(5, "0").localeCompare(b.Beginn.padStart(5, "0"))));
    setBeginn(""); setEnde("");
  }

  async function speichern() {
    const liste = jagddatum ? jagdabschnitte(jagddatum, abschnitte) : null;
    if (!liste) { setAbweisung("unzulaessige-angaben"); return; }
    setAbweisung(null);
    const daten: Record<string, unknown> = { "Drückjagd": id, Jagdabschnitte: liste };
    if (aendern && grund.trim()) daten["Grund"] = grund.trim();
    const a = await befehl(aendern ? "JagdzeitplanAendern" : "JagdzeitplanFestlegen", daten);
    if (!a.ok) { setAbweisung(abweisungsgrund(a)); return; }
    schliessen();
  }

  return (
    <Dialog name="Jagdzeitplan" symbol="uhr" offen={offen} onSchliessen={schliessen}>
      <div className="formular">
        {abschnitte.length ? (
          <ul className="liste-zeilen" aria-label="Jagdabschnitte">
            {abschnitte.map((a, i) => (
              <li key={`${a.Beginn}-${a.Ende}-${i}`}>
                <span className="zeitstempel liste-zeilen__text">{`${a.Beginn} – ${a.Ende}`}</span>
                {bearbeitbar ? <Knopf art="planung-zweit" onPress={() => setEntwurf(abschnitte.filter((_, j) => j !== i))}>Entfernen</Knopf> : null}
              </li>
            ))}
          </ul>
        ) : <Leer text="Kein Jagdzeitplan" symbol="uhr" />}
        {bearbeitbar ? (
          <>
            <div className="formular__paar">
              <Textfeld label="Beginn" wert={beginn} onChange={setBeginn} typ="time" />
              <Textfeld label="Ende" wert={ende} onChange={setEnde} typ="time" />
            </div>
            <Knopf art="planung-zweit" onPress={hinzufuegen}>Hinzufügen</Knopf>
            {aendern ? <Textfeld label="Grund" wert={grund} onChange={setGrund} /> : null}
          </>
        ) : null}
        <Abweisung grund={abweisung} />
        <div className="knopfreihe">
          {bearbeitbar ? <Knopf onPress={() => void speichern()} isDisabled={abschnitte.length === 0}>Speichern</Knopf> : null}
          <Knopf art="zweit" onPress={schliessen}>{bearbeitbar ? "Abbrechen" : "Schließen"}</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
